import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApiSettings } from '../config/api-settings';
import { OrgContextService, SessionService, NotificationService } from '../services';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private orgContext: OrgContextService,
    private session: SessionService,
    private router: Router,
    private notificationService: NotificationService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(ApiSettings.fullPath)) {
      return next.handle(req);
    }

    const orgSlug = this.orgContext.getOrgSlug();
    let headers = req.headers;
    if (orgSlug) headers = headers.set('X-Organization', orgSlug);

    const authedReq = req.clone({ headers, withCredentials: true });

    return next.handle(authedReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // A 401 while we believed there was a session is a real,
          // unexpected expiry worth a toast + redirect. A 401 while we
          // didn't (session.user already empty) is the expected answer
          // for a signed-out request, not news the redirect used to
          // fire unconditionally here, which is what looped against
          // guestGuard: guestGuard's own "are they signed in?" probe
          // would 401, land here, and get redirected straight back to
          // the page guestGuard was already trying to show. Gating on
          // wasSignedIn (and authGuard/guestGuard no longer calling the
          // network at all once hasSessionHint() already says "no", see
          // both guards) closes that loop at its root.
          const wasSignedIn = !!this.session.user;
          this.orgContext.clear();
          this.session.clear();
          if (wasSignedIn) {
            this.notificationService.error('Your session has expired. Please sign in again.');
            this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
          }
        }
        return throwError(() => err);
      }),
    );
  }
}
